import React, { useEffect, useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import MeaPage from "../../components/MeaPage";
import { useAuthContext } from "../../hooks/useAuthContext";
import trainingsAPI from "../../services/trainings";
import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import MeaLink from "../../components/MeaLink";
import accountsAPI from "../../services/accounts";

export function TeacherClassesTrainingsPage() {
  const { school } = useLoaderData();
  const navigate = useNavigate();
  const { auth, currentMembership, updateCurrentMembershipPreferences } = useAuthContext();

  const trainingsQuery = trainingsAPI.useGetList(
    school?.id,
    auth?.access_token,
    1,
    100,
    ""
  );

  const trainings = trainingsQuery?.data?.data || [];
  const lastVisitedTrainingId = currentMembership?.preferences?.last_visited_training;
  const [navigatingTrainingId, setNavigatingTrainingId] = useState(null);
  const isAutoRedirecting = trainingsQuery.isSuccess && (
    (lastVisitedTrainingId && trainings.some(t => t.id === lastVisitedTrainingId)) ||
    trainings.length === 1
  );

  useEffect(() => {
    if (trainingsQuery.isSuccess && lastVisitedTrainingId && trainings.some(t => t.id === lastVisitedTrainingId)) {
      navigate(`/school/${school?.id}/teacher/classes/${lastVisitedTrainingId}`, { replace: true });
    } else if (trainingsQuery.isSuccess && trainings.length === 1) {
      navigate(`/school/${school?.id}/teacher/classes/${trainings[0].id}`, { replace: true });
    }
  }, [trainingsQuery.isSuccess, trainings, lastVisitedTrainingId, school?.id, navigate]);

  const handleSelectTraining = async (trainingId) => {
    setNavigatingTrainingId(trainingId);
    try {
      const updatedPrefs = await accountsAPI.UpdateMemberPreferences(auth?.access_token, currentMembership?.id, trainingId);
      if (updatedPrefs) {
        updateCurrentMembershipPreferences(updatedPrefs);
      } else {
        const fallbackPrefs = currentMembership?.preferences 
          ? { ...currentMembership.preferences, last_visited_training: trainingId }
          : { last_visited_training: trainingId };
        updateCurrentMembershipPreferences(fallbackPrefs);
      }
    } catch (err) {
      console.error("Failed to save training preference", err);
    }
    navigate(`/school/${school?.id}/teacher/classes/${trainingId}`);
  };

  const breadcrumb = [
    {
      label: "Espace Enseignant",
      template: () => (
        <MeaLink to={`/school/${school?.id}/teacher`} icon="pi pi-home" isRipple={true}>
          Espace Enseignant
        </MeaLink>
      ),
    },
    { label: "Mes classes" },
  ];

  if (navigatingTrainingId || isAutoRedirecting) {
    return (
      <MeaPage
        showBackButton={true}
        onBackButtonClick={() => navigate(`/school/${school?.id}/teacher`)}
        title="Sélectionner la formation"
        breadcrumb={<BreadCrumb model={breadcrumb} />}
      >
        <div className="grid grid-nogutter pt-4 px-4 md:px-6 lg:px-8">
          <div className="col-12 p-3">
            <Skeleton width="240px" height="2rem" className="mb-2" />
            <Skeleton width="100%" height="1rem" className="mb-4" />
          </div>
          {[1, 2, 3].map((index) => (
            <div key={index} className="col-12 md:col-6 xl:col-4 p-3">
              <div className="surface-card shadow-1 border-round p-3 h-full">
                <div className="flex align-items-center">
                  <Skeleton width="48px" height="48px" className="mr-3 border-round" />
                  <div className="flex-1">
                    <Skeleton width="70%" height="1.5rem" className="mb-2" />
                    <Skeleton width="40%" height="1rem" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </MeaPage>
    );
  }

  return (
    <MeaPage
      showBackButton={true}
      onBackButtonClick={() => navigate(`/school/${school?.id}/teacher`)}
      title="Sélectionner la formation"
      breadcrumb={<BreadCrumb model={breadcrumb} />}
    >
      <div className="grid grid-nogutter pt-4 px-4 md:px-6 lg:px-8">
        <div className="col-12 p-3">
          <h2 className="text-2xl text-900 font-bold mb-1">Mes classes</h2>
          <p className="text-600 text-sm mb-4">
            Veuillez sélectionner la formation pour laquelle vous souhaitez consulter vos classes.
          </p>
        </div>

        {trainingsQuery.isLoading && (
          <div className="col-12 p-3">
            <Skeleton width="100%" height="150px" />
          </div>
        )}

        {trainingsQuery.isSuccess && trainings.length === 0 && (
          <div className="col-12 p-3 text-center my-5 w-full">
            <i className="pi pi-info-circle text-400 text-5rem mb-3"></i>
            <div className="text-900 text-xl font-bold mb-1">Aucune formation active</div>
            <div className="text-600 text-sm">Il n'y a pas de formation ouverte actuellement pour cette école.</div>
          </div>
        )}

        {trainingsQuery.isSuccess && trainings.length > 1 && (
          <div className="grid w-full">
            {trainings.map((training) => (
              <div key={training.id} className="col-12 md:col-6 xl:col-4 p-3">
                <Card
                  className="cursor-pointer hover:shadow-4 hover:border-teal-500 transition-all transition-duration-200 h-full"
                  onClick={() => handleSelectTraining(training.id)}
                >
                  <div className="flex align-items-center">
                    <div
                      className="mr-3 flex align-items-center justify-content-center bg-teal-50 text-teal-600 border-round"
                      style={{ width: "48px", height: "48px" }}
                    >
                      <i className="pi pi-calendar text-xl"></i>
                    </div>
                    <div className="flex flex-column align-items-start">
                      <span className="text-xl text-900 font-bold mb-1">{training.name}</span>
                      <span className="text-600 text-xs font-semibold uppercase">
                        {training.status}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </MeaPage>
  );
}

export default TeacherClassesTrainingsPage;
